'use client';

import { useEffect, useMemo, useState, useCallback } from "react";
import Masonry from "react-masonry-css";
import clsx from "clsx";
import { AnimateOnView } from "@/components/ui/AnimateOnView";
import { ArtworkModal } from "@/components/sections/ArtworkModal";
import { useCategoryFilter } from "@/context/CategoryFilterContext";
import { useDeviceLimit } from "@/hooks/useDeviceLimit";
import { useInfiniteArtworks, type Artwork, type ArtworkTab } from "@/hooks/useInfiniteArtworks";
import { LazyArtworkCard } from "@/components/ui/LazyArtworkCard";
import { ArtworkSkeletonGrid } from "@/components/ui/ArtworkSkeleton";
import { Pagination } from "@/components/ui/Pagination";

const tabs: { key: ArtworkTab; label: string; mobileLabel: string }[] = [
  { key: "destaques", label: "Em Alta", mobileLabel: "Alta" },
  { key: "novos", label: "Novos arquivos", mobileLabel: "Novos" },
  { key: "exclusivos", label: "Gratuitos", mobileLabel: "Grátis" }
];

const breakpointColumns = {
  default: 5,
  1536: 5,
  1280: 5,
  1024: 4,
  768: 3,
  640: 2
};

export default function MasonryGallery() {
  const [activeTab, setActiveTab] = useState<ArtworkTab>("destaques");
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const { selectedCategoryId, selectedCategoryName, registerResetCallback, unregisterResetCallback } = useCategoryFilter();

  // Get device-appropriate page size (50 for desktop, 30 for mobile)
  const { pageSize, isMobile } = useDeviceLimit();

  // Use infinite scroll hook for paginated loading
  const {
    artworks,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    goToPage,
    reset,
    refetch,
    pagination
  } = useInfiniteArtworks({
    tab: activeTab,
    categoryId: selectedCategoryId,
    pageSize
  });

  // Register reset callback for Header logo click
  useEffect(() => {
    const handleReset = async () => {
      setActiveTab("destaques");
      // Use refetch instead of reset to ensure data is loaded
      await refetch();
    };
    registerResetCallback(handleReset);
    return () => {
      unregisterResetCallback();
    };
  }, [registerResetCallback, unregisterResetCallback, refetch]);

  // Related artworks for modal
  const relatedArtworks = useMemo(() => {
    if (!selectedArtwork) {
      return [];
    }
    return artworks
      .filter(artwork => artwork.id !== selectedArtwork.id)
      .slice(0, 6);
  }, [selectedArtwork, artworks]);

  // Handle tab change
  const handleTabChange = useCallback((tab: ArtworkTab) => {
    setActiveTab(tab);
  }, []);

  // Handle artwork selection
  const handleArtworkSelect = useCallback((artwork: Artwork) => {
    setSelectedArtwork(artwork);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setSelectedArtwork(null);
  }, []);

  // Trigger resize for Masonry recalculation
  useEffect(() => {
    const resizeEvent = () => window.dispatchEvent(new Event("resize"));
    const timeoutId = window.setTimeout(resizeEvent, 80);
    return () => window.clearTimeout(timeoutId);
  }, []);

  // Trigger resize on tab change
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 50);
    return () => window.clearTimeout(timeoutId);
  }, [activeTab]);

  // Calculate skeleton count based on device
  const skeletonCount = isMobile ? 6 : 12;

  return (
    <section id="artes">
      <AnimateOnView className="animate__faster flex flex-col gap-8">
        {/* Header with title and tabs */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl sm:text-3xl font-semibold text-white">
              {selectedCategoryName ? `${selectedCategoryName}` : 'Artes que estão bombando'}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-white/70 hidden sm:block">
              {selectedCategoryName
                ? `Explorando artes da categoria ${selectedCategoryName}`
                : 'Descubra arquivos otimizados para campanhas, com estruturas prontas para edição rápida.'}
            </p>
          </div>
          
          {/* Tab buttons */}
          <div className="flex justify-center gap-2 sm:gap-3 lg:justify-end">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                className={clsx(
                  "relative rounded-full px-3 py-1.5 text-xs font-bold transition-all sm:px-5 sm:py-2.5 sm:text-sm",
                  activeTab === tab.key
                    ? "bg-brand-secondary text-brand-dark shadow-[0_0_15px_rgba(34,197,94,0.4)]"
                    : tab.key === "exclusivos"
                      ? "border border-brand-secondary/50 text-brand-secondary hover:bg-brand-secondary/10"
                      : "border border-white/10 text-slate-400 hover:bg-white/5 hover:text-white"
                )}
              >
                <span className="sm:hidden">{tab.mobileLabel}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Gallery content */}
        <div>
          {/* Initial loading state */}
          {loading ? (
            <Masonry
              breakpointCols={breakpointColumns}
              className="my-masonry-grid"
              columnClassName="my-masonry-grid_column"
            >
              <ArtworkSkeletonGrid count={skeletonCount} />
            </Masonry>
          ) : error ? (
            /* Error state */
            <div className="rounded-3xl border border-red-500/20 bg-red-500/5 p-12 text-center">
              <p className="text-red-400">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-4 rounded-full border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/5"
              >
                Tentar novamente
              </button>
            </div>
          ) : artworks.length === 0 ? (
            /* Empty state */
            <div className="rounded-3xl border border-white/10 bg-brand-surface/50 p-12 text-center">
              <p className="text-slate-400">
                Nenhuma arte encontrada nesta categoria ainda.
              </p>
            </div>
          ) : (
            /* Masonry grid with lazy-loaded cards */
            <>
              <Masonry
                breakpointCols={breakpointColumns}
                className="my-masonry-grid"
                columnClassName="my-masonry-grid_column"
              >
                {artworks.map((artwork) => (
                  <LazyArtworkCard
                    key={artwork.id}
                    artwork={artwork}
                    onClick={handleArtworkSelect}
                  />
                ))}
              </Masonry>

              {/* Numeric pagination */}
              {pagination.pages > 1 && (
                <div className="mt-8 flex flex-col items-center gap-4">
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.pages}
                    onPageChange={goToPage}
                    disabled={loading || loadingMore}
                    maxVisible={isMobile ? 3 : 7}
                    showFirstLast
                  />
                </div>
              )}
            </>
          )}
        </div>
      </AnimateOnView>

      {/* Artwork modal */}
      {selectedArtwork && (
        <ArtworkModal
          artwork={selectedArtwork as any}
          relatedArtworks={relatedArtworks as any}
          onClose={handleModalClose}
          onSwitchArtwork={(art: any) => setSelectedArtwork(art)}
        />
      )}
    </section>
  );
}
